import { Box, Collapsible, Group, HStack, Input, Stack } from '@chakra-ui/react'
import { format } from 'date-fns'
import type { FC, FormEventHandler } from 'react'
import { useState } from 'react'
import { RiImageAddFill } from 'react-icons/ri'
import { useLocation, useNavigate } from 'react-router-dom'
import type { FormProps } from 'react-router-dom'

import { Button } from '@app/components/ui/button'
import { Field } from '@app/components/ui/field'
import { NativeSelectField, NativeSelectRoot } from '@app/components/ui/native-select'
import { toaster } from '@app/components/ui/toaster'
import useTrack from '@app/hooks/useTrack'
import ContributorAutocompleteInput from '@app/next/forms/autocomplete/contributorAutocompleteInput'
import LabelAutocompleteInput from '@app/next/forms/autocomplete/labelAutocompleteInput'
import FormAlert from '@app/next/forms/formAlert'
import RichInput from '@app/next/forms/richInput'
import type { Props as ClassificationsProps } from '@app/shared/entities/classifications'
import Classifications from '@app/shared/entities/classifications'
import ExistingEntityAttachmentUploadButton from '@app/shared/entities/existingEntityAttachmentUploadButton'
import NewEntityAttachmentUploadButton from '@app/shared/entities/newEntityAttachmentUploadButton'
import { EntityMarkdownView } from '@app/shared/entities/shared'
import { UploadsContextProvider } from '@app/shared/entities/uploadsContext'
import EntityImages from '@app/shared/entity/entityImages'
import NewEntityImages from '@app/shared/entity/newEntityImages'
import { useStore } from '@app/store'
import type { DomainEntity } from '@app/types'
import unpackErrors from '@app/utils/unpackErrors'
import { EntityCreate, EntityUpdate } from '@graphql/documents/entity.graphql'
import type { EntityCreateMutation } from '@graphql/queries'
import type { Label } from '@graphql/types'
import { ImpactEnum, ImportanceEnum } from '@graphql/types'

const labelToPickerItem = (label) => ({ id: label, name: label, label })

type EntityClassifications = Partial<Pick<ClassificationsProps, 'classifications'>>
type EntityLabels = Partial<Label>[]

interface ClassificationsInputProps {
  entity: EntityClassifications
}

const ClassificationsInput: FC<ClassificationsInputProps> = ({ entity }) => (
  <Field label="Classifications">
    <Box w="100%">
      <Classifications classifications={entity?.classifications} />
    </Box>
  </Field>
)

const uploadButtonContent = (
  <Button variant="surface" size="sm" aria-label="Upload Attachments">
    <RiImageAddFill />
    Upload image
  </Button>
)

const parseEventInput = (input) => {
  const eventInput = { ...input }

  eventInput.dueDate &&= new Date(eventInput.dueDate)
  if (eventInput.dueDate === '') {
    eventInput.dueDate = null
  }

  eventInput.happenedAt &&= new Date(eventInput.happenedAt)
  eventInput.labels &&= JSON.parse(eventInput.labels)
  eventInput.contributors &&= JSON.parse(eventInput.contributors)
  eventInput.attachmentHashes &&= JSON.parse(eventInput.attachmentHashes)
  if (eventInput.confidenceRating === '') {
    eventInput.confidenceRating = null
  }

  return eventInput
}

interface EntityFormProps extends Partial<FormProps> {
  entity?: Omit<
    DomainEntity,
    | 'organization'
    | 'commentsCount'
    | 'classifications'
    | 'thumbnail'
    | 'labels'
    | 'rfId'
    | 'containerEntityContainments'
  > &
    EntityClassifications & { labels?: EntityLabels }
  nodeId?: string
  containerType?: string
  onCancel?: () => void
  onSuccess?: () => void
  redirectOnCreate?: (entity: Omit<DomainEntity, 'owner'>) => string | null
  redirectOnUpdate?: (entity: Omit<DomainEntity, 'owner'>) => string | null
}

const EntityForm: FC<EntityFormProps> = ({
  nodeId = null,
  containerType = null,
  entity = null,
  onCancel,
  onSuccess,
  children,
  redirectOnCreate = (_event) => '..',
  redirectOnUpdate = (_event) => '..',
  ...rest
}) => {
  const location = useLocation()
  const navigate = useNavigate()
  const actionMutation = useStore.use.actionMutation()
  const addObject = useStore.use.addObject()
  const labels = entity?.labels || location?.state?.labels?.map(labelToPickerItem) || []
  const [open, setOpen] = useState(false)
  const [errors, setErrors] = useState<ReturnType<typeof unpackErrors>>({})
  const track = useTrack()

  const handleCancel = () => {
    if (onCancel) {
      onCancel()

      return
    }

    navigate('..', { relative: 'path' })
  }

  const onCreate: FormEventHandler<HTMLFormElement> = async (e) => {
    e.preventDefault()
    setErrors({})

    let additionalData = {}
    switch (containerType) {
      case 'entity':
      case 'metric':
      case 'basicCard':
        additionalData = { containerId: nodeId, containerType }
        break
      default:
        break
    }
    const formData = new FormData(e.currentTarget)
    const input = { ...parseEventInput(Object.fromEntries(formData.entries())), ...additionalData }

    return actionMutation<EntityCreateMutation>(EntityCreate, input).then((resp) => {
      track('entity:created', input)

      if (resp.error) {
        const unpacked = unpackErrors([resp.error])
        setErrors(unpacked)
        return
      }

      const { entity: resultEntity, errors: resultErrors } = resp.data.entityCreate

      if (resultErrors.length) {
        const unpacked = unpackErrors(resultErrors)
        setErrors(unpacked)
        return
      }

      if (onSuccess) {
        onSuccess()
        return
      }

      toaster.create({
        title: 'Creating your event',
        type: 'success'
      })

      const target = redirectOnCreate?.(resultEntity)
      if (target) {
        navigate(target)
      }
    })
  }

  const onUpdate: FormEventHandler<HTMLFormElement> = async (e) => {
    e.preventDefault()
    setErrors({})

    const formData = new FormData(e.currentTarget)

    const input = { ...Object.fromEntries(formData.entries()), entityId: entity.id || nodeId || null }
    const parsedInput = parseEventInput(input)

    // optimistic update
    addObject({
      id: entity.id,
      classType: 'entity',
      ...parsedInput
    })

    return actionMutation(EntityUpdate, parsedInput).then((resp) => {
      track('entity:updated', input)

      if (resp.error) {
        const unpacked = unpackErrors([resp.error])
        setErrors(unpacked)
        return
      }

      const { errors: resultErrors } = resp.data.entityUpdate

      if (resultErrors.length) {
        const unpacked = unpackErrors(resultErrors)
        setErrors(unpacked)
        return
      }

      if (onSuccess) {
        onSuccess()
        return
      }

      toaster.create({
        title: 'Updating your event.',
        type: 'success'
      })

      const target = redirectOnUpdate?.(parsedInput)
      if (target) {
        navigate(target)
      }
    })
  }

  const onSubmit = entity ? onUpdate : onCreate

  const uploadButton = entity ? (
    <ExistingEntityAttachmentUploadButton entityId={entity.id}>
      {uploadButtonContent}
    </ExistingEntityAttachmentUploadButton>
  ) : (
    <NewEntityAttachmentUploadButton>{uploadButtonContent}</NewEntityAttachmentUploadButton>
  )

  const imagePreview = entity ? (
    <EntityImages entity={entity} scrollContainer="entity-form" />
  ) : (
    <NewEntityImages scrollContainer="entity-form" />
  )

  const defaultDate = entity?.happenedAt ? entity.happenedAt : new Date()
  const parseValue = typeof defaultDate === 'string' ? new Date(defaultDate) : defaultDate
  const happenedAt = format(parseValue, "yyyy-MM-dd'T'HH:mm")

  return (
    <UploadsContextProvider>
      <form id="entity-form" onSubmit={onSubmit} {...rest}>
        <FormAlert description={errors?.global?.message} title="Failed to edit the event!" />
        <Stack gap="5">
          <Field label="Title" required invalid={!!errors?.name} errorText={errors?.name?.message}>
            <Input data-cy="entity-name" defaultValue={entity?.name} name="name" placeholder="What happened?" />
          </Field>
          <Field label="When did the event occur">
            <Input defaultValue={happenedAt} name="happenedAt" type="datetime-local" />
          </Field>
          <Field label="Due date" invalid={!!errors?.dueDate} errorText={errors?.dueDate?.message}>
            <Input defaultValue={entity?.dueDate} name="dueDate" placeholder="Due date (optional)" type="date" />
          </Field>
          <HStack>
            <Box w="50%">
              <Field label="Importance" invalid={!!errors?.importance} errorText={errors?.importance?.message}>
                <NativeSelectRoot>
                  <NativeSelectField name="importance" placeholder="" defaultValue={entity?.importance}>
                    <option value={ImportanceEnum.Minor}>Minor</option>
                    <option value={ImportanceEnum.Medium}>Medium</option>
                    <option value={ImportanceEnum.Major}>Major</option>
                  </NativeSelectField>
                </NativeSelectRoot>
              </Field>
            </Box>
            <Box w="50%">
              <Field label="Impact" invalid={!!errors?.impact} errorText={errors?.impact?.message}>
                <NativeSelectRoot>
                  <NativeSelectField name="impact" placeholder="" defaultValue={entity?.impact}>
                    <option value="">None</option>
                    <option value={ImpactEnum.Positive}>Positive Impact</option>
                    <option value={ImpactEnum.Negative}>Negative Impact</option>
                  </NativeSelectField>
                </NativeSelectRoot>
              </Field>
            </Box>
          </HStack>
          <Stack gap={4}>
            <RichInput
              name="summary"
              label="Summary"
              placeholder="Details about what what happened"
              defaultValue={entity?.summary || ''}
              errors={errors}
            />
            {entity?.sourceSummary && (
              <>
                <Button onClick={() => setOpen(!open)}>{open ? 'Hide' : 'Show'} Source Summary</Button>
                <Collapsible.Root open={open}>
                  <Collapsible.Content>
                    <Box p={4} bgColor="bg.muted" rounded="md">
                      <EntityMarkdownView field={entity.sourceSummary} />
                    </Box>
                  </Collapsible.Content>
                </Collapsible.Root>
              </>
            )}
          </Stack>
          <RichInput
            name="goalHypothesis"
            label="Goal / Hypothesis"
            placeholder="What were you thinking might happen or hypothesis for the change"
            defaultValue={entity?.goalHypothesis || ''}
            errors={errors}
          />
          <RichInput
            name="results"
            label="Results"
            placeholder="What actually happened as a result"
            defaultValue={entity?.results || ''}
            errors={errors}
          />
          <Field label="Type" invalid={!!errors?.entityType} errorText={errors?.entityType?.message}>
            <Input defaultValue={entity?.entityType} name="entityType" placeholder="chore, feature, fix, etc." />
          </Field>
          <Field
            label="URL title"
            invalid={!!errors?.linkTitle}
            errorText={errors?.linkTitle?.message}
            helperText="The title of the link to the demonstration"
          >
            <Input defaultValue={entity?.linkTitle} name="linkTitle" placeholder="Demonstration" />
          </Field>
          <Field
            label="URL"
            helperText="The URL of the link of the demonstration"
            invalid={!!errors?.linkUrl}
            errorText={errors?.linkUrl?.message}
          >
            <Input defaultValue={entity?.linkUrl} name="linkUrl" placeholder="https://" />
          </Field>
          {entity?.classifications?.length > 0 && <ClassificationsInput entity={entity} />}
          <LabelAutocompleteInput name="labels" label="Labels" defaultValue={labels} menuPortalTarget={document.body} />
          <ContributorAutocompleteInput
            name="contributors"
            label="Contributors"
            defaultValue={entity?.entityContributions}
          />
          {entity?.entityAttachmentsCount > 0 ? imagePreview : null}
          {children}
          <Group justifyContent="end">
            {uploadButton}
            <Button size="sm" onClick={handleCancel} variant="outline">
              Cancel
            </Button>
            <Button size="sm" type="submit">
              {entity ? 'Update event' : 'Create event'}
            </Button>
          </Group>
        </Stack>
      </form>
    </UploadsContextProvider>
  )
}

export default EntityForm
